import CalendarMonth from '@mui/icons-material/CalendarMonth'
import Person from '@mui/icons-material/Person'
import ExitToApp from '@mui/icons-material/ExitToApp'
import AccessTime from '@mui/icons-material/AccessTime'
import People from '@mui/icons-material/People'
import MeetingRoom from '@mui/icons-material/MeetingRoom'
import LocalHospital from '@mui/icons-material/LocalHospital'
import LocationOn from '@mui/icons-material/LocationOn'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Settings from '@mui/icons-material/Settings'
import EventNote from '@mui/icons-material/EventNote'
import BarChart from '@mui/icons-material/BarChart'
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Typography,
  Divider,
  Tooltip,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Badge,
  Chip,
  CircularProgress,
  Switch,
  FormControlLabel,
  Button,
} from '@mui/material'
import { styled as muiStyled } from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useContext, useEffect, useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { CheckoutButtonComponent } from 'src/clinic/components/CheckIn/checkInComponents'
import { MOMENT_FORMATS } from 'src/consumer/constants'
import { DocSideContext } from 'src/context/DocSideContextProvider'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { getImageLink, logoutHandlerMentor, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { GenericDisplayFlex, GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import PortalSwitcherButton from '../MentorHeader/PortalSwitcherButton'
import {
  AvailabilityPageContainer,
  PageMainContentContainer,
} from '../MentorAvailabilityPage/MentorAvailabilityPageStyles'
import { HeaderContainer, HeaderGreeting, HeaderLinkText } from '../MentorHeader/MentorHeaderStyles'
import { ClinicChoosingContainer } from './MentorClinicBookingStyles'
import ClinicLocationStatsDrawer from './component/ClinicLocationStatsDrawer'
import MentorAssociationClinicSelectionBox from './component/MentorAssociationClinicSelectionBox'
import ClinicReportsWithVisualizations from './component/ClinicReportsWithVisualizations'
import { getMPDataWithoutParams } from 'src/mentor/services'
import MentorPastEventsListing from '../MentorEventsPage/components/bookingListing/MentorPastEventsListing'
import { SESSION_PLATFORM_ONLINE, SESSION_PLATFORM_OFFLINE, BOOKING_STATUSES, BOOKING_PAYMENT_STATUSES } from 'src/consumer/constants'
import { getTeacherChamberRoomScreensStatus } from 'src/consumer/services/featureServices'

// Styled Tabs matching mentor portal theme - responsive
const MentorTabs = muiStyled(Tabs, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  backgroundColor: 'rgba(116, 80, 170, 0.04)',
  borderRadius: '8px',
  padding: isMobile ? '2px' : '4px',
  minHeight: 'auto',
  width: '100%',
  overflowX: isMobile ? 'auto' : 'visible',
  '& .MuiTabs-indicator': {
    backgroundColor: 'rgba(116, 80, 170, 0.95)',
    height: '3px',
    borderRadius: '3px',
  },
  '& .MuiTabs-flexContainer': {
    gap: isMobile ? '2px' : '4px',
    overflowX: isMobile ? 'auto' : 'visible',
    display: 'flex',
    width: '100%',
    justifyContent: isMobile ? 'flex-start' : 'space-between',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    order: 3,
  },
}))

const MentorTab = muiStyled(Tab, {
  shouldForwardProp: (prop) => prop !== 'isMobile' && prop !== 'iconOnly',
})(({ theme, isMobile, iconOnly }) => ({
    textTransform: 'none',
    minWidth: iconOnly ? '48px' : isMobile ? '120px' : 'auto',
    flex: iconOnly ? '0 0 auto' : isMobile ? '0 1 auto' : '1 1 0',
    maxWidth: iconOnly ? '48px' : 'none',
    fontWeight: theme.typography.fontWeightRegular,
    padding: iconOnly ? '8px' : isMobile ? '8px 12px' : '10px 16px',
    color: 'rgba(71, 85, 105, 0.85)',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    fontSize: isMobile ? '12px' : '14px',
    fontFamily: "'Inter', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    whiteSpace: 'nowrap',
    display: 'flex !important',
    visibility: 'visible !important',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
    '& .MuiTab-iconWrapper': {
      marginRight: iconOnly ? 0 : '8px',
      marginBottom: iconOnly ? 0 : '0px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Only apply icon-only styles when iconOnly is explicitly true
    ...(iconOnly === true && {
      // Hide label text spans, but keep icon wrapper visible
      '& > span:not(.MuiTab-iconWrapper)': {
        display: 'none !important',
      },
      '& .MuiTab-iconWrapper': {
        display: 'flex !important',
        marginRight: '0 !important',
      },
    }),
    '&:hover': {
      color: 'rgba(116, 80, 170, 0.95)',
      backgroundColor: 'rgba(116, 80, 170, 0.08)',
      opacity: 1,
    },
    '&.Mui-selected': {
      color: 'rgba(116, 80, 170, 0.95)',
      fontWeight: theme.typography.fontWeightMedium,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'rgba(116, 80, 170, 0.12)',
    },
  }),
)

const OPEN_IDS = {
  brand: 'brand',
  location: 'location',
  profile: 'profile',
}

// Styled Clinic Brand Card - Modernized
// Note: isSingle is filtered out to prevent React DOM warning
const ClinicBrandCard = muiStyled(Card, {
  shouldForwardProp: (prop) => prop !== 'isSingle',
})(({ theme, isSingle }) => ({
  width: '100%',
  maxWidth: isSingle ? '1200px' : '400px',
  margin: '0 auto',
  borderRadius: '20px',
  boxShadow: '0 2px 12px rgba(116, 80, 170, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1.5px solid rgba(116, 80, 170, 0.12)',
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, rgba(116, 80, 170, 0.6) 0%, rgba(116, 80, 170, 0.3) 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    boxShadow: '0 8px 32px rgba(116, 80, 170, 0.2)',
    transform: 'translateY(-6px)',
    borderColor: 'rgba(116, 80, 170, 0.35)',
    '&::before': {
      opacity: 1,
    },
  },
}))

const ClinicLocationCard = muiStyled(Card)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid rgba(16, 185, 129, 0.15)',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(16, 185, 129, 0.4)',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)',
  },
}))

export default function MentorClinicAssociationsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  const [showClinicStats, setShowClinicStats] = useState(false)
  const [selectedClinicLocationForClinicStats, setSelectedClinicLocationForClinicStats] =
    useState(null)
  const [waitingPatientsData, setWaitingPatientsData] = useState({})
  const [loadingPatientsData, setLoadingPatientsData] = useState(false)
  const [checkedInDetails, setCheckedInDetails] = useState(null)
  const [chamberRoomScreenStatusesList, setChamberRoomScreenStatusesList] = useState([])
  // Move dialog state to parent to prevent loss on child re-renders
  const [checkInDialogState, setCheckInDialogState] = useState({ open: false, clinicId: null, roomData: null, clinicLocationData: null })
  // Sub-tabs for manage-bookings page
  const [manageBookingsSubTab, setManageBookingsSubTab] = useState(0) // 0: All Booked, 1: In Clinic, 2: Video Call, 3: Not Confirmed

  // Ensure DocSide is visible on this page
  const { setShowDocSide } = useContext(DocSideContext)
  
  useEffect(() => {
    setShowDocSide(true)
  }, [setShowDocSide])

  // Check for checked-in status
  useEffect(() => {
    const checkCheckedInStatus = () => {
      try {
        const checkedInData = localStorage.getItem('checkedInDetails')
        if (checkedInData) {
          const parsed = JSON.parse(checkedInData)
          setCheckedInDetails(parsed)
        } else {
          setCheckedInDetails(null)
        }
      } catch (error) {
        console.error('Error parsing checked-in details:', error)
        setCheckedInDetails(null)
      }
    }

    // Check initially
    checkCheckedInStatus()

    // Listen for storage changes
    const handleStorageChange = () => {
      checkCheckedInStatus()
    }

    window.addEventListener('storage', handleStorageChange)

    // Also check periodically (in case of same-tab updates)
    // Increased interval to 5 seconds to reduce unnecessary re-renders that can close dialogs
    const interval = setInterval(checkCheckedInStatus, 5000)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearInterval(interval)
    }
  }, [])

  const [useNewVersion, setUseNewVersion] = useState(() => {
    // Default to new version for all users (including doctors) unless explicitly set to false
    const saved = localStorage.getItem('mentorClinicAssociationsNewVersion')
    return saved !== 'false' // Default to true unless explicitly set to false
  })

  const { mentorClinicAssociationData, mentorProfileData, mentorClinicChambersData } =
    useContext(MentorProfileContext)

  const handleVersionToggle = (event) => {
    const newValue = event.target.checked
    setUseNewVersion(newValue)
    localStorage.setItem('mentorClinicAssociationsNewVersion', newValue.toString())
  }

  const [anchorEl, setAnchorEl] = useState(null)
  const [openId, setOpenId] = useState(null)
  const open = Boolean(anchorEl)
  const handleClick = (event, openId) => {
    setAnchorEl(event.currentTarget)
    setOpenId(openId)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  // Determine active tab based on current route
  const currentTab = location.pathname.includes('preferences-and-settings')
    ? 'preferences'
    : location.pathname.includes('manage-my-bookings')
    ? 'manage-bookings'
    : location.pathname.includes('stats')
    ? 'stats'
    : 'bookings'

  const handleTabChange = (event, newValue) => {
    if (newValue === 'bookings') {
      navigate('/mentor-home/my-clinic-associations')
    } else if (newValue === 'preferences') {
      navigate('/mentor-home/preferences-and-settings')
    } else if (newValue === 'stats') {
      navigate('/mentor-home/stats')
    } else if (newValue === 'manage-bookings') {
      navigate('/mentor-home/manage-my-bookings')
    }
  }

  // Single fetch for all chamber room screen statuses (API returns all rooms in one response)
  const refreshChamberStatuses = useCallback(async () => {
    const list = await getTeacherChamberRoomScreensStatus()
    setChamberRoomScreenStatusesList(list)
  }, [])

  useEffect(() => {
    if (!mentorClinicAssociationData?.length) return
    const controller = new AbortController()
    const fetchStatuses = async () => {
      const list = await getTeacherChamberRoomScreensStatus(controller.signal)
      if (!controller.signal.aborted) setChamberRoomScreenStatusesList(list)
    }
    fetchStatuses()
    const statusInterval = setInterval(fetchStatuses, 30000)
    return () => {
      clearInterval(statusInterval)
      controller.abort()
    }
  }, [mentorClinicAssociationData?.length])

  // Fetch waiting patients for all clinic locations
  useEffect(() => {
    const fetchAllWaitingPatients = async () => {
      if (!mentorClinicAssociationData?.length || !mentorProfileData?.id) return

      setLoadingPatientsData(true)
      const dataMap = {}

      try {
        // Fetch all booked events for today for the mentor
        const todayStart = dayjs().startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
        const todayEnd = dayjs().endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
        
        const apiUrl = `/api/secure/teacher/booked-slots/all-with-patient-info?startTime=${todayStart}&endTime=${todayEnd}&get-patient-details=true`
        
        const response = await getMPDataWithoutParams(apiUrl)
        
        if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
          const allBookedEvents = response?.data?.data?.bookedEvents || []
          const allPatients = response?.data?.data?.patientInfo || []
          
          // Process each clinic location
          mentorClinicAssociationData.forEach((location) => {
            const key = `${location.clinicBrandId}-${location.id}`
            
            // Filter events for this specific clinic location and mentor
            const waitingEvents = allBookedEvents
              .filter((event) => {
                return (
                  event?.clinicId === location.id &&
                  event?.clinicBrandId === location.clinicBrandId &&
                  event?.mentorId === mentorProfileData.id &&
                  !event?.markedCompleted &&
                  event?.status !== 'cancelled'
                )
              })
              .map((event) => {
                const patient = allPatients?.find((p) => p?.id === event?.patientId)
                return {
                  ...event,
                  patientInfo: patient || null,
                }
              })
              .sort((a, b) => {
                // Sort by startTime
                const timeA = dayjs(a.startTime)
                const timeB = dayjs(b.startTime)
                return timeA.isBefore(timeB) ? -1 : timeA.isAfter(timeB) ? 1 : 0
              })

            // Get total booking count (including completed) for this location
            const totalBookings = allBookedEvents.filter((event) => {
              return (
                event?.clinicId === location.id &&
                event?.clinicBrandId === location.clinicBrandId &&
                event?.mentorId === mentorProfileData.id &&
                event?.status !== 'cancelled'
              )
            }).length

            dataMap[key] = {
              count: waitingEvents.length,
              totalBookings: totalBookings,
              earliestTime: waitingEvents.length > 0 ? waitingEvents[0].startTime : null,
              events: waitingEvents,
            }
          })
        }
      } catch (error) {
        console.error('Error fetching waiting patients:', error)
        // Initialize with empty data for all locations
        mentorClinicAssociationData.forEach((location) => {
          const key = `${location.clinicBrandId}-${location.id}`
          dataMap[key] = { count: 0, earliestTime: null, events: [] }
        })
      }

      setWaitingPatientsData(dataMap)
      setLoadingPatientsData(false)
    }

    fetchAllWaitingPatients()
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchAllWaitingPatients, 30000)
    return () => clearInterval(interval)
  }, [mentorClinicAssociationData, mentorProfileData?.id])

  // Helper function to check if a clinic location is currently open
  const isClinicLocationOpen = (location) => {
    const scheduleTimezone = location?.schedules?.[0]?.timezone
      ? decodeURIComponent(location?.schedules?.[0]?.timezone)
      : '+05:30'

    const weeklyHourMap = location?.schedules?.[0]?.weeklyHourMap
    if (!weeklyHourMap) return false

    const currentDay = dayjs().format('dddd').toLowerCase()
    const todayTimings = weeklyHourMap?.[currentDay]

    if (!todayTimings || !todayTimings?.available) return false

    const slotList = todayTimings?.slotList
    if (!slotList || slotList.length === 0) return false

    const now = dayjs().utcOffset(scheduleTimezone)

    // Check all slots for today
    for (const slot of slotList) {
      const [hours, minutes] = slot?.startTime?.split(':') || [0, 0]
      const [endHours, endMinutes] = slot?.endTime?.split(':') || [0, 0]

      // Set the date to today in the clinic's timezone
      const todayStart = now.startOf('day')
      const slotStart = todayStart.hour(parseInt(hours)).minute(parseInt(minutes)).second(0)
      const slotEnd = todayStart.hour(parseInt(endHours)).minute(parseInt(endMinutes)).second(0)

      // Check if we're currently in this slot
      if (now.isAfter(slotStart) && now.isBefore(slotEnd)) {
        return true
      }
    }

    return false
  }

  const uniqueBrands = useMemo(() => {
    const brands = [
    ...new Map(
      mentorClinicAssociationData?.map((item) => [
        item?.clinicBrandId,
        {
          id: item?.clinicBrandId,
          title: item?.clinicBrandName,
          ownerUserIds: item?.clinicBrandOwnerUserIds,
          icon: item?.clinicBrandIconImage || item?.clinicBrandIcon || item?.icon,
          coverIcon: item?.clinicBrandCoverIcon || item?.coverIcon,
          coverImage: item?.clinicBrandCoverImage || item?.clinicBrandImage || item?.coverImage,
          locations: mentorClinicAssociationData?.filter(
            (loc) => loc?.clinicBrandId === item?.clinicBrandId,
          ),
        },
      ]),
    ).values(),
  ]

    // Enhance brands with waiting patients data and open status
    return brands
      .map((brand) => {
        let totalWaitingPatients = 0
        let earliestTime = null
        let hasOpenLocation = false

        brand.locations.forEach((location) => {
          const key = `${brand.id}-${location.id}`
          const data = waitingPatientsData[key]
          if (data) {
            totalWaitingPatients += data.count
            if (data.earliestTime) {
              if (!earliestTime || dayjs(data.earliestTime).isBefore(dayjs(earliestTime))) {
                earliestTime = data.earliestTime
              }
            }
          }
          // Check if this location is currently open
          if (isClinicLocationOpen(location)) {
            hasOpenLocation = true
          }
        })

        return {
          ...brand,
          totalWaitingPatients,
          earliestTime,
          hasOpenLocation,
        }
      })
      .sort((a, b) => {
        // First priority: Earliest upcoming patient time (nulls last)
        if (!a.earliestTime && !b.earliestTime) {
          // Second priority: Open clinics (those that are currently open)
          if (a.hasOpenLocation && !b.hasOpenLocation) return -1
          if (!a.hasOpenLocation && b.hasOpenLocation) return 1
          // If both have same open status, sort by waiting patients count
          return b.totalWaitingPatients - a.totalWaitingPatients
        }
        if (!a.earliestTime) return 1
        if (!b.earliestTime) return -1
        
        // Both have earliest times - compare them
        const timeDiff = dayjs(a.earliestTime).diff(dayjs(b.earliestTime), 'minute')
        
        // If times are the same or very close (within 5 minutes), apply second priority
        if (Math.abs(timeDiff) <= 5) {
          // Second priority: Open clinics (those that are currently open)
          if (a.hasOpenLocation && !b.hasOpenLocation) return -1
          if (!a.hasOpenLocation && b.hasOpenLocation) return 1
          // If both have same open status, maintain time order
          return timeDiff
        }
        
        // Times are significantly different, sort by earliest time
        return timeDiff
      })
  }, [mentorClinicAssociationData, waitingPatientsData])

  const isSingleBrand = uniqueBrands.length === 1

  function navigateToHotline(clinicLocationData) {
    navigate(
      `/hotline-for-mentor/${clinicLocationData?.id}/${
        mentorClinicChambersData?.find(
          (chamberItem) => chamberItem?.clinicId === clinicLocationData?.id,
        )?.satelliteScreenId
      }`,
    )
  }

  useEffect(() => {
    if (mentorClinicAssociationData && mentorClinicAssociationData?.length > 0) {
    } else {
      navigate('/mentor-home')
    }
  }, [])

  // Render old version (original implementation)
  const renderOldVersion = () => {
    const oldUniqueBrands = [
      ...new Map(
        mentorClinicAssociationData?.map((item) => [
          item?.clinicBrandId,
          {
            id: item?.clinicBrandId,
            title: item?.clinicBrandName,
            ownerUserIds: item?.clinicBrandOwnerUserIds,
          },
        ]),
      ).values(),
    ]

    return (
      <>
        <div
          style={{
            width: 'calc(100% - 40px)',
            maxWidth: '980px',
            margin: '0px auto 40px',
            gap: '8px',
          }}
          className="d-flex justify-content-end mb-2"
        >
          <CheckoutButtonComponent />
          <IconButton
            onClick={(e) => {
              handleClick(e, OPEN_IDS.brand)
            }}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open && openId === OPEN_IDS.brand}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              onClick={() => {
                navigate('/clinic-home')
              }}
            >
              {getAllRolesInBoolean().isClinicOwner ? 'Add another ' : 'Create your own'} clinic brand
              +
            </MenuItem>
          </Menu>
        </div>
        {oldUniqueBrands?.map((brand) => (
          <PageMainContentContainer
            className="d-flex flex-column align-items-center p-3"
            key={brand?.id}
          >
            <h4>{brand?.title}</h4>
            {getAllRolesInBoolean().isClinicOwner &&
              brand.ownerUserIds?.includes(mentorProfileData?.userId) && (
                <div style={{ display: 'flex', justifyContent: 'right', width: '100%' }}>
                  <IconButton
                    onClick={(e) => {
                      handleClick(e, OPEN_IDS.location)
                    }}
                    size="small"
                    style={{ marginLeft: 'auto' }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={open && openId === OPEN_IDS.location}
                    onClose={handleClose}
                    onClick={handleClose}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                          width: 32,
                          height: 32,
                          ml: -0.5,
                          mr: 1,
                        },
                        '&:before': {
                          content: '""',
                          display: 'block',
                          position: 'absolute',
                          top: 0,
                          right: 14,
                          width: 10,
                          height: 10,
                          bgcolor: 'background.paper',
                          transform: 'translateY(-50%) rotate(45deg)',
                          zIndex: 0,
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem
                      onClick={() => {
                        navigate('/aumhum-for-clinics')
                      }}
                    >
                      Create new Clinic Location
                    </MenuItem>
                  </Menu>
                </div>
              )}
            <ClinicChoosingContainer>
              {mentorClinicAssociationData
                ?.filter((loc) => {
                  return loc?.clinicBrandId === brand?.id
                })
                ?.map((item, index) => {
                  return (
                    <MentorAssociationClinicSelectionBox
                      key={`clinic-${item?.id || index}`}
                      clinicLocationData={item}
                      showJoinHotline={mentorClinicChambersData?.some(
                        (chamberItem) => chamberItem?.clinicId === item?.id,
                      )}
                      hotlineOnClick={navigateToHotline}
                      showCheckInPerson
                      showViewStatsButton={
                        getAllRolesInBoolean().isClinicOwner &&
                        brand.ownerUserIds?.includes(mentorProfileData?.userId)
                      }
                      onClickViewStatsButton={(clinicLocationId) => {
                        setShowClinicStats(true)
                        setSelectedClinicLocationForClinicStats(clinicLocationId)
                      }}
                      checkedInDetails={checkedInDetails}
                      screenStatusesList={chamberRoomScreenStatusesList}
                      onRefreshChamberStatuses={refreshChamberStatuses}
                    />
                  )
                })}
            </ClinicChoosingContainer>
          </PageMainContentContainer>
        ))}
      </>
    )
  }

  // Render new version (enhanced implementation)
  const renderNewVersion = () => {
    return (
      <>
        {loadingPatientsData && uniqueBrands.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={40} sx={{ color: 'rgba(16, 185, 129, 0.8)' }} />
            </Box>
          ) : isSingleBrand ? (
            // Single Brand - Optimized Layout with Card
            <Box>
              {uniqueBrands.map((brand) => {
                const firstLocation = brand.locations?.[0]
                const hasMultipleLocations = brand.locations?.length > 1
                const isCheckedInBrand =
                  checkedInDetails?.clinicBrandId === brand.id

                return (
                  <ClinicBrandCard
                    key={brand.id}
                    isSingle={true}
                    sx={{
                      mb: 4,
                      ...(isCheckedInBrand && {
                        border: '2px solid rgba(116, 80, 170, 0.5)',
                        boxShadow: '0 8px 32px rgba(116, 80, 170, 0.25)',
                        background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)',
                        transform: 'scale(1.01)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: '0 12px 40px rgba(116, 80, 170, 0.3)',
                        },
                      }),
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                  <Box
                    sx={{
                      mb: 0,
                    }}
                  >
                    {/* Brand Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, minWidth: 0 }}>
                          {(() => {
                            const brandImage = getImageLink(
                              brand.coverIcon || brand.icon,
                              brand.coverImage,
                              false,
                            )
                            return brandImage ? (
                              <Box
                                component="img"
                                src={brandImage}
                                alt={brand.title}
                                sx={{
                                  width: { xs: 48, sm: 56 },
                                  height: { xs: 48, sm: 56 },
                                  borderRadius: '14px',
                                  objectFit: 'cover',
                                  border: '1px solid rgba(116, 80, 170, 0.2)',
                                  flexShrink: 0,
                                  background: 'rgba(116, 80, 170, 0.05)',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: { xs: 48, sm: 56 },
                                  height: { xs: 48, sm: 56 },
                                  borderRadius: '14px',
                                  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.08) 100%)',
                                  border: '1px solid rgba(116, 80, 170, 0.2)',
                                  flexShrink: 0,
                                }}
                              >
                                <LocalHospital
                                  sx={{
                                    fontSize: { xs: 24, sm: 28 },
                                    color: 'rgba(116, 80, 170, 0.9)',
                                  }}
                                />
                              </Box>
                            )
                          })()}
                          <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: 700,
                                color: 'rgba(15, 23, 42, 0.95)',
                                fontSize: { xs: '24px', sm: '28px', md: '32px' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                              }}
                            >
                              {brand.title}
                            </Typography>
                          </Box>
                        </Box>
                        {brand.totalWaitingPatients > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Chip
                              icon={<People sx={{ fontSize: '16px !important' }} />}
                              label={`${brand.totalWaitingPatients} patient${
                                brand.totalWaitingPatients !== 1 ? 's' : ''
                              } waiting`}
                              color="primary"
                              sx={{
                                backgroundColor: 'rgba(116, 80, 170, 0.1)',
                                color: 'rgba(116, 80, 170, 0.95)',
                                fontWeight: 600,
                                fontSize: '13px',
                              }}
                            />
                            {brand.earliestTime && (
                              <Chip
                                icon={<AccessTime sx={{ fontSize: '16px !important' }} />}
                                label={`Next: ${dayjs.utc(brand.earliestTime).local().format('h:mm A')}`}
                                sx={{
                                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                  color: 'rgba(59, 130, 246, 0.95)',
                                  fontWeight: 500,
                                  fontSize: '12px',
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                      {getAllRolesInBoolean().isClinicOwner &&
                        brand.ownerUserIds?.includes(mentorProfileData?.userId) && (
                          <IconButton
                            onClick={(e) => {
                              handleClick(e, OPEN_IDS.location)
                            }}
                            size="small"
                            sx={{
                              border: '1px solid rgba(16, 185, 129, 0.2)',
                              '&:hover': {
                                backgroundColor: 'rgba(16, 185, 129, 0.08)',
                              },
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        )}
                    </Box>

                    {/* Quick Access for Single Location */}
                    {!hasMultipleLocations && firstLocation ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          gap: 3,
                          flexWrap: 'wrap',
                          position: 'relative',
                        }}
                      >
                        {(() => {
                          const key = `${brand.id}-${firstLocation.id}`
                          const locationData = waitingPatientsData[key]
                          const bookingCount = locationData?.totalBookings || 0
                          const waitingCount = locationData?.count || 0
                          const isCheckedInLocation =
                            checkedInDetails?.clinicLocationId === firstLocation.id &&
                            checkedInDetails?.clinicBrandId === brand.id
                          return (
                            <Box
                              sx={{
                                position: 'relative',
                                width: { xs: '100%', sm: '100%' },
                                maxWidth: { xs: '100%', sm: '600px' },
                                border: isCheckedInLocation
                                  ? '2px solid rgba(116, 80, 170, 0.5)'
                                  : '1.5px solid rgba(116, 80, 170, 0.18)',
                                borderRadius: { xs: '14px', sm: '16px' },
                                overflow: 'hidden',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                background: isCheckedInLocation
                                  ? 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
                                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
                                boxShadow: isCheckedInLocation
                                  ? '0 8px 24px rgba(116, 80, 170, 0.2), 0 2px 8px rgba(116, 80, 170, 0.1)'
                                  : '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.08)',
                                ...(isCheckedInLocation && {
                                  transform: 'translateY(-2px)',
                                }),
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '3px',
                                  background: isCheckedInLocation
                                    ? 'linear-gradient(90deg, rgba(116, 80, 170, 0.8) 0%, rgba(116, 80, 170, 0.4) 100%)'
                                    : 'linear-gradient(90deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.1) 100%)',
                                  opacity: isCheckedInLocation ? 1 : 0,
                                  transition: 'opacity 0.3s ease',
                                },
                                '&:hover': {
                                  borderColor: isCheckedInLocation
                                    ? 'rgba(116, 80, 170, 0.65)'
                                    : 'rgba(116, 80, 170, 0.35)',
                                  boxShadow: isCheckedInLocation
                                    ? '0 12px 32px rgba(116, 80, 170, 0.25), 0 4px 12px rgba(116, 80, 170, 0.15)'
                                    : '0 4px 16px rgba(116, 80, 170, 0.12), 0 2px 6px rgba(116, 80, 170, 0.08)',
                                  transform: isCheckedInLocation ? 'translateY(-3px) scale(1.01)' : 'translateY(-2px) scale(1.005)',
                                  '&::before': {
                                    opacity: 1,
                                  },
                                },
                              }}
                            >
                              {isCheckedInLocation && (
                                <Chip
                                  icon={<MeetingRoom sx={{ fontSize: '12px !important' }} />}
                                  label="Checked In"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    left: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    backgroundColor: 'rgba(116, 80, 170, 0.95)',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: { xs: '9px', sm: '10px' },
                                    height: { xs: '22px', sm: '24px' },
                                    boxShadow: '0 3px 10px rgba(116, 80, 170, 0.4)',
                                    border: '1px solid rgba(116, 80, 170, 0.3)',
                                    '& .MuiChip-icon': {
                                      marginLeft: '6px',
                                      color: 'white',
                                    },
                                  }}
                                />
                              )}
                              {bookingCount > 0 && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    right: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    alignItems: 'flex-end',
                                  }}
                                >
                                  {waitingCount > 0 && (
                                    <Chip
                                      label={waitingCount}
                                      size="small"
                                      color="error"
                                      sx={{
                                        fontSize: { xs: '9px', sm: '10px' },
                                        fontWeight: 700,
                                        height: { xs: '20px', sm: '22px' },
                                        minWidth: { xs: '20px', sm: '22px' },
                                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                                        animation: waitingCount > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
                                        '@keyframes pulse': {
                                          '0%, 100%': {
                                            transform: 'scale(1)',
                                          },
                                          '50%': {
                                            transform: 'scale(1.05)',
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                  <Chip
                                    icon={<People sx={{ fontSize: '12px !important' }} />}
                                    label={`${bookingCount} Patient${bookingCount !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                      fontSize: { xs: '9px', sm: '10px' },
                                      fontWeight: 600,
                                      height: { xs: '20px', sm: '22px' },
                                      backgroundColor: 'rgba(59, 130, 246, 0.12)',
                                      color: 'rgba(59, 130, 246, 0.95)',
                                      border: '1px solid rgba(59, 130, 246, 0.2)',
                                      '& .MuiChip-icon': {
                                        marginLeft: '4px',
                                      },
                                    }}
                                  />
                                </Box>
                              )}
                              <MentorAssociationClinicSelectionBox
                                clinicLocationData={firstLocation}
                                showJoinHotline={mentorClinicChambersData?.some(
                                  (chamberItem) => chamberItem?.clinicId === firstLocation?.id,
                                )}
                                hotlineOnClick={navigateToHotline}
                                showCheckInPerson
                                showViewStatsButton={
                                  getAllRolesInBoolean().isClinicOwner &&
                                  brand.ownerUserIds?.includes(mentorProfileData?.userId)
                                }
                                onClickViewStatsButton={(clinicLocationId) => {
                                  setShowClinicStats(true)
                                  setSelectedClinicLocationForClinicStats(clinicLocationId)
                                }}
                                containerCustomStyles={{
                                  maxWidth: '100%',
                                  width: '100%',
                                  padding: { xs: '12px 10px', sm: '14px 12px' },
                                  background: 'transparent',
                                  border: 'none',
                                  boxShadow: 'none',
                                }}
                                checkedInDetails={checkedInDetails}
                                screenStatusesList={chamberRoomScreenStatusesList}
                                onRefreshChamberStatuses={refreshChamberStatuses}
                              />
                            </Box>
                          )
                        })()}
                      </Box>
                    ) : (
                      // Multiple Locations in Single Brand
                      <ClinicChoosingContainer>
                        {brand.locations?.map((item, index) => {
                          const key = `${brand.id}-${item.id}`
                          const locationData = waitingPatientsData[key]
                          const bookingCount = locationData?.totalBookings || 0
                          const waitingCount = locationData?.count || 0
                          const isCheckedInLocation =
                            checkedInDetails?.clinicLocationId === item.id &&
                            checkedInDetails?.clinicBrandId === brand.id
                          return (
                            <Box
                              key={`location-${item?.id || index}`}
                              sx={{
                                position: 'relative',
                                width: { xs: '100%', sm: 'auto' },
                                maxWidth: { xs: '100%', sm: '400px' },
                                border: isCheckedInLocation
                                  ? '2px solid rgba(116, 80, 170, 0.5)'
                                  : '1.5px solid rgba(116, 80, 170, 0.18)',
                                borderRadius: { xs: '14px', sm: '16px' },
                                overflow: 'hidden',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                background: isCheckedInLocation
                                  ? 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
                                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
                                boxShadow: isCheckedInLocation
                                  ? '0 8px 24px rgba(116, 80, 170, 0.2), 0 2px 8px rgba(116, 80, 170, 0.1)'
                                  : '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.08)',
                                ...(isCheckedInLocation && {
                                  transform: 'translateY(-2px)',
                                }),
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '3px',
                                  background: isCheckedInLocation
                                    ? 'linear-gradient(90deg, rgba(116, 80, 170, 0.8) 0%, rgba(116, 80, 170, 0.4) 100%)'
                                    : 'linear-gradient(90deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.1) 100%)',
                                  opacity: isCheckedInLocation ? 1 : 0,
                                  transition: 'opacity 0.3s ease',
                                },
                                '&:hover': {
                                  borderColor: isCheckedInLocation
                                    ? 'rgba(116, 80, 170, 0.65)'
                                    : 'rgba(116, 80, 170, 0.35)',
                                  boxShadow: isCheckedInLocation
                                    ? '0 12px 32px rgba(116, 80, 170, 0.25), 0 4px 12px rgba(116, 80, 170, 0.15)'
                                    : '0 4px 16px rgba(116, 80, 170, 0.12), 0 2px 6px rgba(116, 80, 170, 0.08)',
                                  transform: isCheckedInLocation ? 'translateY(-3px) scale(1.01)' : 'translateY(-2px) scale(1.005)',
                                  '&::before': {
                                    opacity: 1,
                                  },
                                },
                              }}
                            >
                              {/* Booking Count Badges - Enhanced */}
                              {bookingCount > 0 && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    right: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    alignItems: 'flex-end',
                                  }}
                                >
                                  {waitingCount > 0 && (
                                    <Chip
                                      label={waitingCount}
                                      size="small"
                                      color="error"
                                      sx={{
                                        fontSize: { xs: '9px', sm: '10px' },
                                        fontWeight: 700,
                                        height: { xs: '20px', sm: '22px' },
                                        minWidth: { xs: '20px', sm: '22px' },
                                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                                        animation: waitingCount > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
                                        '@keyframes pulse': {
                                          '0%, 100%': {
                                            transform: 'scale(1)',
                                          },
                                          '50%': {
                                            transform: 'scale(1.05)',
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                  <Chip
                                    icon={<People sx={{ fontSize: '12px !important' }} />}
                                    label={`${bookingCount} Patient${bookingCount !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                      fontSize: { xs: '9px', sm: '10px' },
                                      fontWeight: 600,
                                      height: { xs: '20px', sm: '22px' },
                                      backgroundColor: 'rgba(59, 130, 246, 0.12)',
                                      color: 'rgba(59, 130, 246, 0.95)',
                                      border: '1px solid rgba(59, 130, 246, 0.2)',
                                      '& .MuiChip-icon': {
                                        marginLeft: '4px',
                                      },
                                    }}
                                  />
                                </Box>
                              )}
                              {isCheckedInLocation && (
                                <Chip
                                  icon={<MeetingRoom sx={{ fontSize: '12px !important' }} />}
                                  label="Checked In"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    left: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    backgroundColor: 'rgba(116, 80, 170, 0.95)',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: { xs: '9px', sm: '10px' },
                                    height: { xs: '22px', sm: '24px' },
                                    boxShadow: '0 3px 10px rgba(116, 80, 170, 0.4)',
                                    border: '1px solid rgba(116, 80, 170, 0.3)',
                                    '& .MuiChip-icon': {
                                      marginLeft: '6px',
                                      color: 'white',
                                    },
                                  }}
                                />
                              )}
                              <MentorAssociationClinicSelectionBox
                                clinicLocationData={item}
                                showJoinHotline={mentorClinicChambersData?.some(
                                  (chamberItem) => chamberItem?.clinicId === item?.id,
                                )}
                                hotlineOnClick={navigateToHotline}
                                showCheckInPerson
                                showViewStatsButton={
                                  getAllRolesInBoolean().isClinicOwner &&
                                  brand.ownerUserIds?.includes(mentorProfileData?.userId)
                                }
                                onClickViewStatsButton={(clinicLocationId) => {
                                  setShowClinicStats(true)
                                  setSelectedClinicLocationForClinicStats(clinicLocationId)
                                }}
                                containerCustomStyles={{
                                  maxWidth: '100%',
                                  width: '100%',
                                  padding: { xs: '12px 10px', sm: '14px 12px' },
                                  background: 'transparent',
                                  border: 'none',
                                  boxShadow: 'none',
                                }}
                                checkedInDetails={checkedInDetails}
                                screenStatusesList={chamberRoomScreenStatusesList}
                                onRefreshChamberStatuses={refreshChamberStatuses}
                              />
                            </Box>
                          )
                        })}
                      </ClinicChoosingContainer>
                    )}

                    {/* Location Menu */}
                    {getAllRolesInBoolean().isClinicOwner &&
                      brand.ownerUserIds?.includes(mentorProfileData?.userId) && (
                        <Menu
                          anchorEl={anchorEl}
                          id="account-menu"
                          open={open && openId === OPEN_IDS.location}
                          onClose={handleClose}
                          onClick={handleClose}
                          PaperProps={{
                            elevation: 0,
                            sx: {
                              overflow: 'visible',
                              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                              mt: 1.5,
                              borderRadius: '12px',
                              '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                              },
                            },
                          }}
                          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                          <MenuItem
                            onClick={() => {
                              navigate('/aumhum-for-clinics')
                              handleClose()
                            }}
                            sx={{
                              fontSize: '14px',
                              '&:hover': {
                                backgroundColor: 'rgba(16, 185, 129, 0.08)',
                              },
                            }}
                          >
                            Create new Clinic Location
                          </MenuItem>
                        </Menu>
                      )}
                    </Box>
                  </CardContent>
                </ClinicBrandCard>
                )
              })}
            </Box>
          ) : (
            // Multiple Brands - Card Grid Layout
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr', // Single column on mobile for better usability
                  sm: 'repeat(2, 1fr)', // 2 columns on small tablets
                  md: 'repeat(3, 1fr)', // 3 columns on larger screens
                },
                gap: { xs: 2.5, sm: 3 }, // Slightly increased gap on mobile
                mb: 4,
              }}
            >
              {uniqueBrands.map((brand) => {
                const firstLocation = brand.locations?.[0]
                const isCheckedInBrand =
                  checkedInDetails?.clinicBrandId === brand.id
                return (
                  <ClinicBrandCard
                    key={brand.id}
                    isSingle={false}
                    sx={{
                      ...(isCheckedInBrand && {
                        border: '2px solid rgba(116, 80, 170, 0.5)',
                        boxShadow: '0 8px 32px rgba(116, 80, 170, 0.25)',
                        background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)',
                        transform: 'scale(1.02)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.03)',
                          boxShadow: '0 12px 40px rgba(116, 80, 170, 0.3)',
                        },
                      }),
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                      {/* Brand Header - Modernized */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 2.5,
                          gap: 2,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          {/* Brand Name with Icon */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, minWidth: 0 }}>
                            {(() => {
                              const brandImage = getImageLink(
                                brand.coverIcon || brand.icon,
                                brand.coverImage,
                                false,
                              )
                              return brandImage ? (
                                <Box
                                  component="img"
                                  src={brandImage}
                                  alt={brand.title}
                                  sx={{
                                    width: { xs: 40, sm: 48 },
                                    height: { xs: 40, sm: 48 },
                                    borderRadius: '12px',
                                    objectFit: 'cover',
                                    border: '1px solid rgba(116, 80, 170, 0.2)',
                                    flexShrink: 0,
                                    background: 'rgba(116, 80, 170, 0.05)',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: { xs: 40, sm: 48 },
                                    height: { xs: 40, sm: 48 },
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.08) 100%)',
                                    border: '1px solid rgba(116, 80, 170, 0.2)',
                                    flexShrink: 0,
                                  }}
                                >
                                  <LocalHospital
                                    sx={{
                                      fontSize: { xs: 20, sm: 24 },
                                      color: 'rgba(116, 80, 170, 0.9)',
                                    }}
                                  />
                                </Box>
                              )
                            })()}
                            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 700,
                                  color: 'rgba(15, 23, 42, 0.95)',
                                  fontSize: { xs: '18px', sm: '22px', md: '24px' },
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1.2,
                                }}
                              >
                                {brand.title}
                              </Typography>
                              {brand.locations?.length > 0 && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'rgba(100, 116, 139, 0.7)',
                                    fontSize: { xs: '11px', sm: '12px' },
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  <LocationOn sx={{ fontSize: '14px' }} />
                                  {brand.locations.length} location{brand.locations.length !== 1 ? 's' : ''}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Stats Row - Modernized */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                            {brand.totalWaitingPatients > 0 && (
                              <Chip
                                icon={<People sx={{ fontSize: '14px !important' }} />}
                                label={`${brand.totalWaitingPatients} waiting`}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(116, 80, 170, 0.12)',
                                  color: 'rgba(116, 80, 170, 0.95)',
                                  fontWeight: 600,
                                  fontSize: { xs: '10px', sm: '11px' },
                                  height: { xs: '24px', sm: '26px' },
                                  border: '1px solid rgba(116, 80, 170, 0.2)',
                                  '& .MuiChip-icon': {
                                    marginLeft: '8px',
                                  },
                                }}
                              />
                            )}
                            {brand.earliestTime && (
                              <Chip
                                icon={<AccessTime sx={{ fontSize: '14px !important' }} />}
                                label={`Next: ${dayjs.utc(brand.earliestTime).local().format('h:mm A')}`}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                  color: 'rgba(59, 130, 246, 0.95)',
                                  fontWeight: 600,
                                  fontSize: { xs: '10px', sm: '11px' },
                                  height: { xs: '24px', sm: '26px' },
                                  border: '1px solid rgba(59, 130, 246, 0.2)',
                                  '& .MuiChip-icon': {
                                    marginLeft: '8px',
                                  },
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                        {getAllRolesInBoolean().isClinicOwner &&
                          brand.ownerUserIds?.includes(mentorProfileData?.userId) && (
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation()
                                handleClick(e, `${OPEN_IDS.location}-${brand.id}`)
                              }}
                              size="small"
                              sx={{
                                ml: 1,
                                border: '1px solid rgba(116, 80, 170, 0.2)',
                                borderRadius: '10px',
                                '&:hover': {
                                  backgroundColor: 'rgba(116, 80, 170, 0.08)',
                                  borderColor: 'rgba(116, 80, 170, 0.3)',
                                },
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                      </Box>

                      {/* Show All Locations Directly - Modernized */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: { xs: 1.5, sm: 2 },
                          mt: { xs: 2, sm: 2.5 },
                          pt: { xs: 2, sm: 2.5 },
                          borderTop: '1px solid rgba(116, 80, 170, 0.1)',
                        }}
                      >
                        {brand.locations?.map((location, locIndex) => {
                          const key = `${brand.id}-${location.id}`
                          const locationData = waitingPatientsData[key]
                          const bookingCount = locationData?.totalBookings || 0
                          const waitingCount = locationData?.count || 0
                          const isCheckedInLocation =
                            checkedInDetails?.clinicLocationId === location.id &&
                            checkedInDetails?.clinicBrandId === brand.id

                          return (
                            <Box
                              key={locIndex}
                              sx={{
                                position: 'relative',
                                width: { xs: '100%', sm: 'auto' },
                                maxWidth: { xs: '100%', sm: 'none' },
                                border: isCheckedInLocation
                                  ? '2px solid rgba(116, 80, 170, 0.5)'
                                  : '1.5px solid rgba(116, 80, 170, 0.18)',
                                borderRadius: { xs: '14px', sm: '16px' },
                                overflow: 'hidden',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                background: isCheckedInLocation
                                  ? 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
                                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
                                boxShadow: isCheckedInLocation
                                  ? '0 8px 24px rgba(116, 80, 170, 0.2), 0 2px 8px rgba(116, 80, 170, 0.1)'
                                  : '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.08)',
                                ...(isCheckedInLocation && {
                                  transform: 'translateY(-2px)',
                                }),
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '3px',
                                  background: isCheckedInLocation
                                    ? 'linear-gradient(90deg, rgba(116, 80, 170, 0.8) 0%, rgba(116, 80, 170, 0.4) 100%)'
                                    : 'linear-gradient(90deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.1) 100%)',
                                  opacity: isCheckedInLocation ? 1 : 0,
                                  transition: 'opacity 0.3s ease',
                                },
                                '&:hover': {
                                  borderColor: isCheckedInLocation
                                    ? 'rgba(116, 80, 170, 0.65)'
                                    : 'rgba(116, 80, 170, 0.35)',
                                  boxShadow: isCheckedInLocation
                                    ? '0 12px 32px rgba(116, 80, 170, 0.25), 0 4px 12px rgba(116, 80, 170, 0.15)'
                                    : '0 4px 16px rgba(116, 80, 170, 0.12), 0 2px 6px rgba(116, 80, 170, 0.08)',
                                  transform: isCheckedInLocation ? 'translateY(-3px) scale(1.01)' : 'translateY(-2px) scale(1.005)',
                                  '&::before': {
                                    opacity: 1,
                                  },
                                },
                              }}
                            >
                              {/* Booking Count Badge - Enhanced */}
                              {bookingCount > 0 && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    right: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    alignItems: 'flex-end',
                                  }}
                                >
                                  {waitingCount > 0 && (
                                    <Chip
                                      label={waitingCount}
                                      size="small"
                                      color="error"
                                      sx={{
                                        fontSize: { xs: '9px', sm: '10px' },
                                        fontWeight: 700,
                                        height: { xs: '20px', sm: '22px' },
                                        minWidth: { xs: '20px', sm: '22px' },
                                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                                        animation: waitingCount > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
                                        '@keyframes pulse': {
                                          '0%, 100%': {
                                            transform: 'scale(1)',
                                          },
                                          '50%': {
                                            transform: 'scale(1.05)',
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                  <Chip
                                    icon={<People sx={{ fontSize: '12px !important' }} />}
                                    label={`${bookingCount} Patient${bookingCount !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                      fontSize: { xs: '9px', sm: '10px' },
                                      fontWeight: 600,
                                      height: { xs: '20px', sm: '22px' },
                                      backgroundColor: 'rgba(59, 130, 246, 0.12)',
                                      color: 'rgba(59, 130, 246, 0.95)',
                                      border: '1px solid rgba(59, 130, 246, 0.2)',
                                      '& .MuiChip-icon': {
                                        marginLeft: '4px',
                                      },
                                    }}
                                  />
                                </Box>
                              )}

                              {isCheckedInLocation && (
                                <Chip
                                  icon={<MeetingRoom sx={{ fontSize: '12px !important' }} />}
                                  label="Checked In"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: { xs: 10, sm: 12 },
                                    left: { xs: 10, sm: 12 },
                                    zIndex: 2,
                                    backgroundColor: 'rgba(116, 80, 170, 0.95)',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: { xs: '9px', sm: '10px' },
                                    height: { xs: '22px', sm: '24px' },
                                    boxShadow: '0 3px 10px rgba(116, 80, 170, 0.4)',
                                    border: '1px solid rgba(116, 80, 170, 0.3)',
                                    '& .MuiChip-icon': {
                                      marginLeft: '6px',
                                      color: 'white',
                                    },
                                  }}
                                />
                              )}
                              <MentorAssociationClinicSelectionBox
                                clinicLocationData={location}
                                showJoinHotline={mentorClinicChambersData?.some(
                                  (chamberItem) => chamberItem?.clinicId === location?.id,
                                )}
                                hotlineOnClick={navigateToHotline}
                                showCheckInPerson
                                showViewStatsButton={false}
                                hideClinicRelatedInfo={false}
                                containerCustomStyles={{
                                  maxWidth: '100%',
                                  width: '100%',
                                  padding: { xs: '12px 10px', sm: '14px 12px' },
                                  background: 'transparent',
                                  border: 'none',
                                  boxShadow: 'none',
                                }}
                                checkedInDetails={checkedInDetails}
                                screenStatusesList={chamberRoomScreenStatusesList}
                                onRefreshChamberStatuses={refreshChamberStatuses}
                              />
                            </Box>
                          )
                        })}
                      </Box>
                    </CardContent>
                  </ClinicBrandCard>
                )
              })}
            </Box>
          )}

          {/* Full Brand Details for Multiple Brands */}
          {!isSingleBrand && (
            <Box sx={{ mt: 6 }}>
              {uniqueBrands.map((brand) => (
                <Box key={brand.id} id={`brand-${brand.id}`} sx={{ mb: 6 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: 'rgba(15, 23, 42, 0.95)',
                        fontSize: { xs: '22px', sm: '24px' },
                      }}
                    >
                      {brand.title}
                    </Typography>
                    {getAllRolesInBoolean().isClinicOwner &&
                      brand.ownerUserIds?.includes(mentorProfileData?.userId) && (
                        <IconButton
                          onClick={(e) => {
                            handleClick(e, `${OPEN_IDS.location}-${brand.id}`)
                          }}
                          size="small"
                          sx={{
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(16, 185, 129, 0.08)',
                            },
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                  </Box>
                  <ClinicChoosingContainer>
                    {brand.locations?.map((item, index) => {
                      const key = `${brand.id}-${item.id}`
                      const locationData = waitingPatientsData[key]
                      const bookingCount = locationData?.totalBookings || 0
                      const waitingCount = locationData?.count || 0
                      const isCheckedInLocation =
                        checkedInDetails?.clinicLocationId === item.id &&
                        checkedInDetails?.clinicBrandId === brand.id
                      return (
                        <Box
                          key={index}
                          sx={{
                            position: 'relative',
                            width: { xs: '100%', sm: 'auto' },
                            maxWidth: { xs: '100%', sm: '400px' },
                            border: isCheckedInLocation
                              ? '2px solid rgba(116, 80, 170, 0.5)'
                              : '1.5px solid rgba(116, 80, 170, 0.18)',
                            borderRadius: { xs: '14px', sm: '16px' },
                            overflow: 'hidden',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            background: isCheckedInLocation
                              ? 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
                              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
                            boxShadow: isCheckedInLocation
                              ? '0 8px 24px rgba(116, 80, 170, 0.2), 0 2px 8px rgba(116, 80, 170, 0.1)'
                              : '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.08)',
                            ...(isCheckedInLocation && {
                              transform: 'translateY(-2px)',
                            }),
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '3px',
                              background: isCheckedInLocation
                                ? 'linear-gradient(90deg, rgba(116, 80, 170, 0.8) 0%, rgba(116, 80, 170, 0.4) 100%)'
                                : 'linear-gradient(90deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.1) 100%)',
                              opacity: isCheckedInLocation ? 1 : 0,
                              transition: 'opacity 0.3s ease',
                            },
                            '&:hover': {
                              borderColor: isCheckedInLocation
                                ? 'rgba(116, 80, 170, 0.65)'
                                : 'rgba(116, 80, 170, 0.35)',
                              boxShadow: isCheckedInLocation
                                ? '0 12px 32px rgba(116, 80, 170, 0.25), 0 4px 12px rgba(116, 80, 170, 0.15)'
                                : '0 4px 16px rgba(116, 80, 170, 0.12), 0 2px 6px rgba(116, 80, 170, 0.08)',
                              transform: isCheckedInLocation ? 'translateY(-3px) scale(1.01)' : 'translateY(-2px) scale(1.005)',
                              '&::before': {
                                opacity: 1,
                              },
                            },
                          }}
                        >
                          {/* Booking Count Badges - Enhanced */}
                          {bookingCount > 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: { xs: 10, sm: 12 },
                                right: { xs: 10, sm: 12 },
                                zIndex: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                alignItems: 'flex-end',
                              }}
                            >
                              {waitingCount > 0 && (
                                <Chip
                                  label={waitingCount}
                                  size="small"
                                  color="error"
                                  sx={{
                                    fontSize: { xs: '9px', sm: '10px' },
                                    fontWeight: 700,
                                    height: { xs: '20px', sm: '22px' },
                                    minWidth: { xs: '20px', sm: '22px' },
                                    boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                                    animation: waitingCount > 0 ? 'pulse 2s ease-in-out infinite' : 'none',
                                    '@keyframes pulse': {
                                      '0%, 100%': {
                                        transform: 'scale(1)',
                                      },
                                      '50%': {
                                        transform: 'scale(1.05)',
                                      },
                                    },
                                  }}
                                />
                              )}
                              <Chip
                                icon={<People sx={{ fontSize: '12px !important' }} />}
                                label={`${bookingCount} Patient${bookingCount !== 1 ? 's' : ''}`}
                                size="small"
                                sx={{
                                  fontSize: { xs: '9px', sm: '10px' },
                                  fontWeight: 600,
                                  height: { xs: '20px', sm: '22px' },
                                  backgroundColor: 'rgba(59, 130, 246, 0.12)',
                                  color: 'rgba(59, 130, 246, 0.95)',
                                  border: '1px solid rgba(59, 130, 246, 0.2)',
                                  '& .MuiChip-icon': {
                                    marginLeft: '4px',
                                  },
                                }}
                              />
                            </Box>
                          )}
                          {isCheckedInLocation && (
                            <Chip
                              icon={<MeetingRoom sx={{ fontSize: '12px !important' }} />}
                              label="Checked In"
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: { xs: 10, sm: 12 },
                                left: { xs: 10, sm: 12 },
                                zIndex: 2,
                                backgroundColor: 'rgba(116, 80, 170, 0.95)',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: { xs: '9px', sm: '10px' },
                                height: { xs: '22px', sm: '24px' },
                                boxShadow: '0 3px 10px rgba(116, 80, 170, 0.4)',
                                border: '1px solid rgba(116, 80, 170, 0.3)',
                                '& .MuiChip-icon': {
                                  marginLeft: '6px',
                                  color: 'white',
                                },
                              }}
                            />
                          )}
                          <MentorAssociationClinicSelectionBox
                            clinicLocationData={item}
                            showJoinHotline={mentorClinicChambersData?.some(
                              (chamberItem) => chamberItem?.clinicId === item?.id,
                            )}
                            hotlineOnClick={navigateToHotline}
                            showCheckInPerson
                            showViewStatsButton={
                              getAllRolesInBoolean().isClinicOwner &&
                              brand.ownerUserIds?.includes(mentorProfileData?.userId)
                            }
                            onClickViewStatsButton={(clinicLocationId) => {
                              setShowClinicStats(true)
                              setSelectedClinicLocationForClinicStats(clinicLocationId)
                            }}
                          containerCustomStyles={{
                            maxWidth: '100%',
                            width: '100%',
                            padding: { xs: '12px 10px', sm: '14px 12px' },
                            background: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                            ...(isCheckedInLocation && {
                              backgroundColor: 'transparent',
                            }),
                          }}
                            checkedInDetails={checkedInDetails}
                            screenStatusesList={chamberRoomScreenStatusesList}
                            onRefreshChamberStatuses={refreshChamberStatuses}
                          />
                        </Box>
                      )
                    })}
                  </ClinicChoosingContainer>
                </Box>
              ))}
            </Box>
          )}

        {/* Location Menu for Multiple Brands */}
        {!isSingleBrand &&
          uniqueBrands.map((brand) => (
            <Menu
              key={`menu-${brand.id}`}
              anchorEl={anchorEl}
              id={`account-menu-${brand.id}`}
              open={open && openId === `${OPEN_IDS.location}-${brand.id}`}
              onClose={handleClose}
              onClick={handleClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  borderRadius: '12px',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem
                onClick={() => {
                  navigate('/aumhum-for-clinics')
                  handleClose()
                }}
                sx={{
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: 'rgba(16, 185, 129, 0.08)',
                  },
                }}
              >
                Create new Clinic Location
              </MenuItem>
            </Menu>
          ))}

        <ClinicLocationStatsDrawer
          open={showClinicStats}
          onClose={() => setShowClinicStats(false)}
          clinicLocationData={selectedClinicLocationForClinicStats}
        />
      </>
    )
  }

  return (
    <>
      {/* Title and Profile/Portal Switcher Row */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1001,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          className: 'hide-on-print-only',
        }}
      >
        <HeaderContainer className="hide-on-print-only">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              width: '100%',
              py: 1.5,
              gap: 2,
            }}
          >
            {/* Left Column - Title */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column' }}>
              <HeaderGreeting
                main
                sx={{
                  fontSize: isMobile ? '16px' : isTablet ? '18px' : '20px',
                  fontWeight: 600,
                }}
              >
                {currentTab === 'bookings'
                  ? 'Clinic Check-In & Bookings'
                  : currentTab === 'stats'
                  ? 'Stats'
                  : currentTab === 'manage-bookings'
                  ? 'Manage Bookings'
                  : 'Preferences & Settings'}
              </HeaderGreeting>
              {currentTab === 'bookings' && (
                <GenericDisplayFlex sx={{ alignItems: 'center', gap: isMobile ? 0.5 : 1, mt: isMobile ? 0.25 : 0.5 }}>
                  <CalendarMonth
                    sx={{ fontSize: isMobile ? 12 : isTablet ? 14 : 16, color: 'rgba(100, 116, 139, 0.8)' }}
                  />
                  <Typography
                    variant={isMobile ? 'caption' : 'body2'}
                    sx={{
                      color: 'rgba(100, 116, 139, 0.8)',
                      fontSize: isMobile ? '11px' : isTablet ? '12px' : '13px',
                      fontWeight: 500,
                    }}
                  >
                    {isMobile
                      ? dayjs().format('MMM DD, YYYY')
                      : dayjs().format(MOMENT_FORMATS.completeDateDetailsWithMonthShortFormAndDay)}
                  </Typography>
                </GenericDisplayFlex>
              )}
            </Box>
            {/* Right Column - Buttons (only as wide as needed) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? 1 : 1.5,
                flexShrink: 0,
                justifyContent: 'flex-end',
              }}
            >
              <PortalSwitcherButton />
              {isMobile ? (
                <IconButton
                  onClick={(e) => handleClick(e, OPEN_IDS.profile)}
                  size="small"
                  sx={{
                    padding: '4px',
                    border: '2px solid rgba(16, 185, 129, 0.2)',
                    backgroundColor: 'rgba(16, 185, 129, 0.04)',
                  }}
                >
                  <Avatar
                    src={getImageLink(mentorProfileData?.profilePicId)}
                    sx={{
                      width: 32,
                      height: 32,
                      border: '2px solid rgba(255, 255, 255, 0.8)',
                    }}
                  >
                    {mentorProfileData?.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </IconButton>
              ) : (
                <Tooltip title="Account Menu" arrow>
                  <IconButton
                    onClick={(e) => handleClick(e, OPEN_IDS.profile)}
                    size="small"
                    sx={{
                      padding: '6px',
                      border: '2px solid rgba(16, 185, 129, 0.2)',
                      backgroundColor: 'rgba(16, 185, 129, 0.04)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                        borderColor: 'rgba(16, 185, 129, 0.4)',
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <Avatar
                      src={getImageLink(mentorProfileData?.profilePicId)}
                      sx={{
                        width: 40,
                        height: 40,
                        border: '2px solid rgba(255, 255, 255, 0.8)',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)',
                      }}
                    >
                      {mentorProfileData?.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </HeaderContainer>
      </Box>

      {/* Tabs Row - Full Width with Proper Spacing */}
      <Box
        sx={{
          width: '100%',
          position: 'sticky',
          top: isMobile ? '80px' : '88px',
          zIndex: 1000,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          className: 'hide-on-print-only',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
          <MentorTabs value={currentTab} onChange={handleTabChange} isMobile={isMobile}>
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="bookings"
              icon={isMobile ? <CalendarMonth /> : undefined}
              label={isMobile ? 'Check-In' : isTablet ? 'Check-In' : 'Clinic Check-In & Bookings'}
            />
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="manage-bookings"
              icon={isMobile ? <EventNote /> : undefined}
              label={isMobile ? 'Bookings' : isTablet ? 'Bookings' : 'Manage Bookings'}
            />
            {getAllRolesInBoolean().isClinicOwner && (
              <MentorTab
                isMobile={isMobile}
                iconOnly={isMobile}
                value="stats"
                icon={isMobile ? <BarChart /> : undefined}
                label={isMobile ? 'Stats' : 'Stats'}
              />
            )}
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="preferences"
              icon={isMobile ? <Settings /> : undefined}
              label={isMobile ? 'Settings' : isTablet ? 'Settings' : 'Preferences & Settings'}
            />
          </MentorTabs>
        </Box>
      </Box>

      {/* Profile Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open && openId === OPEN_IDS.profile}
        onClose={handleClose}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 240,
            borderRadius: '12px',
            border: '1px solid rgba(16, 185, 129, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'visible',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              borderLeft: '1px solid rgba(16, 185, 129, 0.1)',
              borderTop: '1px solid rgba(16, 185, 129, 0.1)',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            backgroundColor: 'rgba(16, 185, 129, 0.02)',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'rgba(15, 23, 42, 0.95)',
              fontSize: '14px',
              mb: 0.5,
            }}
          >
            {mentorProfileData?.name || 'Doctor'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(100, 116, 139, 0.8)',
              fontSize: '12px',
            }}
          >
            Mentor Portal
          </Typography>
        </Box>

        <MenuItem
          onClick={() => {
            navigate('/mentor-home/profile')
            handleClose()
          }}
          sx={{
            py: 1.25,
            px: 2,
            fontSize: '14px',
            color: 'rgba(71, 85, 105, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(16, 185, 129, 0.08)',
              color: 'rgba(16, 185, 129, 0.95)',
            },
          }}
        >
          <Person sx={{ fontSize: 18, mr: 1.5, color: 'rgba(16, 185, 129, 0.7)' }} />
          Profile Settings
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={() => {
            logoutHandlerMentor()
            navigate('/')
            handleClose()
          }}
          sx={{
            py: 1.25,
            px: 2,
            fontSize: '14px',
            color: 'rgba(239, 68, 68, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
              color: 'rgba(239, 68, 68, 1)',
            },
          }}
        >
          <ExitToApp sx={{ fontSize: 18, mr: 1.5 }} />
          Sign Out
        </MenuItem>
      </Menu>

      {/* Checked-In Status Strip - Modernized with Glass Effect */}
      {checkedInDetails && (
        <Box
          sx={{
            width: '100%',
            background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.08) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(116, 80, 170, 0.2)',
            boxShadow: '0 4px 20px rgba(116, 80, 170, 0.1)',
            py: 2.5,
            px: { xs: 2, sm: 3 },
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          <Box
            sx={{
              maxWidth: '1400px',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.2) 0%, rgba(116, 80, 170, 0.1) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(116, 80, 170, 0.3)',
                  boxShadow: '0 4px 12px rgba(116, 80, 170, 0.15)',
                  flexShrink: 0,
                }}
              >
                <MeetingRoom
                  sx={{
                    fontSize: { xs: 24, sm: 28 },
                    color: 'rgba(116, 80, 170, 1)',
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'rgba(15, 23, 42, 0.95)',
                    fontSize: { xs: '16px', sm: '18px', md: '20px' },
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Checked In: {checkedInDetails?.roomName || 'Room'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {(() => {
                    const clinicLocation = mentorClinicAssociationData?.find(
                      (loc) => loc.id === checkedInDetails?.clinicLocationId,
                    )
                    const clinicBrand = uniqueBrands.find(
                      (brand) => brand.id === checkedInDetails?.clinicBrandId,
                    )
                    return (
                      <>
                        {clinicBrand && (
                          <Chip
                            label={clinicBrand.title}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(116, 80, 170, 0.15)',
                              color: 'rgba(116, 80, 170, 0.95)',
                              fontWeight: 600,
                              fontSize: '11px',
                              height: '22px',
                              border: '1px solid rgba(116, 80, 170, 0.3)',
                            }}
                          />
                        )}
                        {clinicLocation && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'rgba(100, 116, 139, 0.9)',
                              fontSize: { xs: '12px', sm: '13px' },
                              fontWeight: 500,
                            }}
                          >
                            {clinicLocation.name}
                          </Typography>
                        )}
                        {checkedInDetails?.createdTime && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'rgba(100, 116, 139, 0.7)',
                              fontSize: { xs: '11px', sm: '12px' },
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <AccessTime sx={{ fontSize: '14px' }} />
                            {dayjs.utc(checkedInDetails.createdTime).local().format('h:mm A')}
                          </Typography>
                        )}
                      </>
                    )
                  })()}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <Button
                variant="contained"
                sx={{
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: { xs: 2.5, sm: 3 },
                  py: 1,
                  fontWeight: 600,
                  fontSize: { xs: '13px', sm: '14px' },
                  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.95) 0%, rgba(116, 80, 170, 0.85) 100%)',
                  boxShadow: '0 4px 12px rgba(116, 80, 170, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(116, 80, 170, 1) 0%, rgba(116, 80, 170, 0.9) 100%)',
                    boxShadow: '0 6px 16px rgba(116, 80, 170, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease',
                }}
                onClick={() => {
                  if (checkedInDetails?.clinicBrandId && checkedInDetails?.clinicLocationId) {
                    navigate(
                      `/mentor-home/my-clinic-associations/${checkedInDetails.clinicBrandId}/${checkedInDetails.clinicLocationId}`,
                    )
                  }
                }}
              >
                Proceed
              </Button>
              <CheckoutButtonComponent
                buttonColor="error"
                styleProp={{
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: { xs: 2.5, sm: 3 },
                  py: 1,
                  fontWeight: 600,
                  fontSize: { xs: '13px', sm: '14px' },
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                }}
                additionalFunctionAfterAction={() => {
                  setCheckedInDetails(null)
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      <AvailabilityPageContainer>
        {/* Action Bar */}
        <Box
          sx={{
          width: 'calc(100% - 40px)',
            maxWidth: '1400px',
            margin: '0 auto 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            padding: { xs: '0 16px', sm: '0 24px' },
            flexWrap: 'wrap',
          }}
        >
          {/* Toggle Switch for New Version - Hide on manage-bookings page */}
          {currentTab !== 'manage-bookings' && (
            <FormControlLabel
              control={
                <Switch
                  checked={useNewVersion}
                  onChange={handleVersionToggle}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'rgba(16, 185, 129, 0.95)',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'rgba(16, 185, 129, 0.5)',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(71, 85, 105, 0.8)' }}>
                  {useNewVersion ? 'New View' : 'Classic View'}
                </Typography>
              }
              sx={{ m: 0 }}
            />
          )}

          {/* 3 Dots Menu - Hide on manage-bookings page */}
          {currentTab !== 'manage-bookings' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!checkedInDetails && <CheckoutButtonComponent />}
              <IconButton
                onClick={(e) => {
                  handleClick(e, OPEN_IDS.brand)
                }}
                size="small"
                sx={{
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(16, 185, 129, 0.08)',
                  },
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          )}
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open && openId === OPEN_IDS.brand}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
                borderRadius: '12px',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem
            onClick={() => {
              navigate('/clinic-home')
                handleClose()
              }}
              sx={{
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: 'rgba(16, 185, 129, 0.08)',
                },
              }}
            >
              {getAllRolesInBoolean().isClinicOwner ? 'Add another ' : 'Create your own'} clinic
              brand +
                  </MenuItem>
                </Menu>
        </Box>

        {/* Main Content - Conditional Rendering */}
        {currentTab === 'manage-bookings' ? (
          <Box
            sx={{
              width: 'calc(100% - 40px)',
              maxWidth: '1400px',
              margin: '0 auto',
              padding: { xs: '0 16px', sm: '0 24px' },
            }}
          >
            {/* Sub-tabs for manage-bookings */}
            <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
              <MentorTabs
                value={manageBookingsSubTab}
                onChange={(e, newValue) => setManageBookingsSubTab(newValue)}
                isMobile={isMobile}
              >
                <MentorTab label="All Booked" isMobile={isMobile} />
                <MentorTab label="In Clinic (Booked)" isMobile={isMobile} />
                <MentorTab label="Video Call (Booked)" isMobile={isMobile} />
                <MentorTab label="Booking Not Confirmed" isMobile={isMobile} />
              </MentorTabs>
            </Box>
            <MentorPastEventsListing
              key={`manage-bookings-${manageBookingsSubTab}`}
              mentorProfileData={mentorProfileData}
              logsApiEndpoint="/api/secure/teacher/booked-event-info/logs/get"
              meetingSrcFilter={
                manageBookingsSubTab === 1
                  ? SESSION_PLATFORM_OFFLINE
                  : manageBookingsSubTab === 2
                  ? SESSION_PLATFORM_ONLINE
                  : undefined
              }
              filterByBookedStatus={
                manageBookingsSubTab === 3 ? false : true // Tab 3 (Not Confirmed) excludes booked, others include only booked
              }
              showOnlyUnconfirmed={manageBookingsSubTab === 3}
              hideSessionTypeFilter={true}
            />
          </Box>
        ) : currentTab === 'stats' && getAllRolesInBoolean().isClinicOwner ? (
          <Box
            sx={{
              width: 'calc(100% - 40px)',
              maxWidth: '1400px',
              margin: '0 auto',
              padding: { xs: '0 16px', sm: '0 24px' },
            }}
          >
            <ClinicReportsWithVisualizations />
          </Box>
        ) : useNewVersion ? (
          <Box
            sx={{
              width: 'calc(100% - 40px)',
              maxWidth: '1400px',
              margin: '0 auto',
              padding: { xs: '0 16px', sm: '0 24px' },
            }}
          >
            {loadingPatientsData && uniqueBrands.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress size={40} sx={{ color: 'rgba(16, 185, 129, 0.8)' }} />
              </Box>
            ) : (
              renderNewVersion()
            )}
          </Box>
        ) : (
          renderOldVersion()
        )}

      <ClinicLocationStatsDrawer
        open={showClinicStats}
        onClose={() => setShowClinicStats(false)}
        clinicLocationData={selectedClinicLocationForClinicStats}
      />
      </AvailabilityPageContainer>
    </>
  )
}
